<template>
  <div class="stake-box"
       v-click-outside="onOutside">
    <div class="header">
      <div>{{ 'general_stake' | translate }}</div>
      <div class="total-payment"
           v-if="showPayment">
        <input type="text"
               ref="stakeBoxPayment"
               @focus="openKeyboard($event)"
               @blur="roundPayment()"
               class="input no-spinner"
               readonly>
        <label>{{ 'general_stake' | translate }}</label>
        <span class="value"
              :class="{highlight: focus}">
          {{ stake ? stake : '' }}
        </span>
        <button class="button" @click="resetPayment()">
          <i class="icon icon-backspace-a"></i>
        </button>
      </div>
      <Keyboard v-if="showKeyboard"
                @keyboardButton="setStakeManually">
      </Keyboard>
    </div>
    <div class="predefined-stakes">
      <button class="button"
              v-for="(value, index) in predefinedStakes"
              @click="setPredefinedPayment(value)"
              :key="index">
        {{ '+' + value }}
      </button>
    </div>
    <div class="actions"
         v-if="showActions">
      <button @click="closeStakeBox()">{{ 'general_cancel' | translate}}</button>
      <button @click="acceptPayment()">{{ 'general_accept' | translate}}</button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ClickOutside from 'vue-click-outside';
import eventBus from '@/utility/eventBus';
import Keyboard from './Keyboard';

export default {
  name: 'StakeBox',
  components: {
    Keyboard,
  },
  directives: {
    ClickOutside,
  },
  props: {
    showPayment: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    predefinedStakes: {
      type: Array,
      default: () => [0.5, 1, 2, 5, 10, 20],
    },
  },
  data() {
    return {
      stake: '',
      manualStake: '',
      showKeyboard: false,
      focus: false,
      specialCharacters: [],
      test: '',
    };
  },
  computed: {
    ...mapGetters([
      'totalPayment',
      'rules',
    ]),
  },
  methods: {
    ...mapActions([
      'setTotalPayment',
    ]),
    onOutside(event) {
      if (!event.target.value) this.$emit('close');
    },
    openKeyboard() {
      this.showKeyboard = true;
      this.focus = true;
      this.$refs.stakeBoxPayment.focus();
    },
    closeStakeBox() {
      eventBus.$emit('openStakeBox');
    },
    resetPayment() {
      this.setPaymentToMin();
      this.clearManualStake();
    },
    setPaymentToMin() {
      this.stake = Number(this.rules.minBetAmount.value).toFixed(2);
    },
    clearManualStake() {
      this.manualStake = '';
    },
    setStakeManually(value) {
      this.setPaymentToMin();
      if (this.focus) this.clearManualStake();
      if (typeof (value) !== 'number') {
        this.specialCharacters.push(value);
      }
      if (value) this.focus = false;
      this.manualStake += value;
      this.stake = this.manualStake;
    },
    roundPayment() {
      this.stake = Number(this.stake).toFixed(2);
    },
    isValidStake() {
      if (this.specialCharacters.length > 1) {
        return false;
      }
      return true;
    },
    setPredefinedPayment(value) {
      this.updatePayment(value);
    },
    updatePayment(value) {
      this.clearManualStake();
      // Check if first char is dot, replace with 0
      if (typeof (this.stake) === 'string' && typeof (this.stake.charAt(0) === 'string')) {
        this.stake = Number(0 + this.stake);
      }
      // Reset stake to the last total payment if it's not valid
      if (!this.isValidStake()) {
        this.stake = this.totalPayment;
      }
      this.stake += Number(value);
      this.stake = Number(this.stake).toFixed(2);
      if (!this.showActions) this.setTotalPayment(this.stake);
      this.specialCharacters = [];
    },
    acceptPayment() {
      if (!this.isValidStake()) {
        this.stake = this.totalPayment;
      }
      this.showKeyboard = false;
      this.setTotalPayment(this.stake);
      eventBus.$emit('openStakeBox');
    },
  },
  created() {
    this.stake = Number(this.totalPayment).toFixed(2);
  },
  watch: {
    totalPayment(value) {
      this.stake = Number(value);
    },
  },
};
</script>

<style lang="scss">
  .stake-box {
    width: 100%;
    background: var(--card-bg);
    position: absolute;
    border-radius: 2px;
    padding: 16px;
    z-index: 1;
    box-shadow: 3px 4px 15px 3px rgba(40, 40, 40, 1);

    .header {
      text-align: center;
      font-size: 14px;
      font-weight: 700;
      color: var(--text-primary-1);

      .total-payment {
        height: 40px;
        margin: 14px 4px 16px 4px;
        display: flex;
        justify-content: space-between;
        position: relative;

        .input {
          border-radius: 2px;
          background-color: var(--input-bg);
          border: none;
          outline: 0;
          position: relative;
          text-indent: 8px;
          padding: 0;
          height: 100%;
          font-size: 14px;
          color: var(--text-primary-1);
          float: left;
          width: calc(100% - 70px);

          &.disabled {
            opacity: 0.5;
          }

          &.highlight {
            color: yellow;
          }
        }

        input {
          font-family: 'Roboto', sans-serif;
        }

        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type="number"] {
          -moz-appearance: textfield;
        }

        .button {
          width: 50px;
          float: right;
        }

        label {
          position: absolute;
          left: 8px;
          top: 3px;
          font-size: 12px;
          font-weight: 400;
          color: var(--text-primary-2);
        }

        .value {
          position: absolute;
          top: 18px;
          left: 9px;
          font-weight: 400;
          font-size: 14px;
          color: var(--text-primary-1);

          &.highlight {
            background: #0097ff;
          }
        }
      }
    }

    .predefined-stakes {
      display: flex;
      width: 100%;
      flex-wrap: wrap;
      margin-top: 8px;

      button {
        flex-grow: 1;
        flex-basis: 80px;
        margin: 0 4px 8px 4px;
      }
    }

    .actions {
      float: right;
      margin-top: 27px;
      padding: 0 5px;
      button {
        border: none;
        outline: none;
        background: transparent;
        color: #fc1;
        text-transform: uppercase;
        margin-left: 43px;
      }
    }
  }

  @media (min-width: 320px) and (max-width: 660px) {
    .stake-box {
      box-shadow: none;

      .predefined-stakes {
        button {
          flex-basis: 87px;
        }
      }
    }
  }
</style>
